import { useEffect, useMemo, useState } from "react";
import { Eye, MapPin, Sparkles, UserPlus } from "lucide-react";
import { fetchEmployees, fetchJobs } from "@/lib/api";
import { PageHeader, RangeToggle, StatusPill, useRange, safeNum, fmtHours } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import EmployeeDetailModal from "@/components/dashboard/EmployeeDetailModal";
import CreateEmployeeDialog from "@/components/dashboard/CreateEmployeeDialog";
import DataSourceBadge from "@/components/dashboard/DataSourceBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

// Employees are managed primarily via the automatic flow.
// "Opret medarbejder" creates the profile — and can send the login-invitation
// in the same flow via the toggle inside the dialog.
export default function Employees() {
    const { user } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [range, setRange] = useRange("week");
    const [search, setSearch] = useState("");
    const [openEmp, setOpenEmp] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    const isAdmin = user && (user.role === "owner" || user.role === "admin");

    const refresh = () => {
        fetchEmployees().then((d) => setEmployees(d || [])).catch(() => {});
        fetchJobs().then((d) => setJobs(d || [])).catch(() => {});
    };

    useEffect(() => { refresh(); }, []);

    const jobDetails = useMemo(() => {
        const map = {};
        jobs.forEach((j) => { map[j.id] = j; });
        return map;
    }, [jobs]);

    const enriched = useMemo(() => employees.map((e) => {
        const currentJob = jobs.find((j) => j.id === e.current_task);
        return { ...e, current_location: currentJob?.address || "—" };
    }), [employees, jobs]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return enriched;
        return enriched.filter((e) =>
            [e.id, e.name, e.role, e.team, e.status, e.current_task, e.current_location].some((v) => (v || "").toString().toLowerCase().includes(q))
        );
    }, [enriched, search]);

    // hours-key per range
    const rangeKey = { week: "week", month: "month", year: "year", total: "year" }[range] || "week";
    const rangeLabel = { week: "uge", month: "måned", year: "år", total: "total" }[range];

    return (
        <div data-testid="employees-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader
                title="Employees"
                subtitle="Medarbejderstatus opdateres automatisk fra medarbejderflowet."
                right={
                    <div className="flex flex-wrap items-center gap-3">
                        <DataSourceBadge entity="employees" />
                        <SearchInput value={search} onChange={setSearch} placeholder="Søg medarbejder…" testid="employees-search" />
                        <RangeToggle value={range} onChange={setRange} testid="employees-range" />
                        {isAdmin && (
                            <Button data-testid="employees-new-btn" onClick={() => setCreateOpen(true)} className="rounded-full h-10 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow">
                                <UserPlus className="w-4 h-4 mr-1.5" /> Opret medarbejder
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="mb-4 bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-blue-900">
                <Sparkles className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                <p>Medarbejdernes status, opgaver og lokation opdateres automatisk fra dataflowet. Brug <strong>Opret medarbejder</strong> til at oprette profilen (billede, priser, telefon) — slå <em>Send login-invitation</em> til i samme flow, hvis medarbejderen skal have login-adgang.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((e) => (
                    <div
                        key={e.id}
                        data-testid={`employee-${e.id}`}
                        onClick={() => setOpenEmp(e)}
                        className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="text-base font-bold text-slate-900">{e.name || "Ukendt"}</h3>
                                <p className="text-xs text-slate-500 mt-0.5">{e.role || "—"} · {e.team || "—"}</p>
                            </div>
                            <StatusPill status={e.status} />
                        </div>
                        <p className="text-sm text-slate-600">Aktuel opgave: <span className="font-medium text-slate-800">{e.current_task || "—"}</span></p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3 text-slate-400" />{e.current_location}</p>
                        <p className="text-xs text-slate-400 mt-1">Sidst opdateret: {e.last_seen || "—"}</p>

                        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-2 text-center">
                            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">I dag</p><p className="text-base font-bold text-slate-900">{fmtHours(e.hours?.day)}</p></div>
                            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Uge</p><p className="text-base font-bold text-slate-900">{fmtHours(e.hours?.week)}</p></div>
                            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Måned</p><p className="text-base font-bold text-slate-900">{fmtHours(e.hours?.month)}</p></div>
                            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">År</p><p className="text-base font-bold text-slate-900">{fmtHours(e.hours?.year)}</p></div>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                            <span className="text-[10px] text-slate-400">Filter: {rangeLabel} · {safeNum(e.hours?.[rangeKey])}t</span>
                            <span className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" />Se detaljer</span>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full text-center py-10 text-slate-400">{search ? "Ingen medarbejdere matcher søgningen." : "Ingen data for valgt periode"}</div>
                )}
            </div>

            <EmployeeDetailModal
                employee={openEmp}
                allJobs={jobs}
                allJobDetails={jobDetails}
                open={!!openEmp}
                onClose={() => setOpenEmp(null)}
                onDeleted={() => refresh()}
            />

            <CreateEmployeeDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => refresh()} />
        </div>
    );
}
